import { QueryClient } from "@tanstack/react-query";

import { PartDto } from "#modules/parts/infra/fetchPart.api";
import {
  invalidatePart,
  updateQuoteRequestLinesForPart,
} from "#modules/parts/infra/invalidatePart";
import { getPartQueryData, setPartQueryData } from "#modules/parts/infra/part.query";
import { addChildrenToQuoteRequestDebounced } from "#modules/quoteRequestLines/infra/addChildrenToQuoteRequestDebounced";
import { invalidateQuoteRequestLinesDebounced } from "#modules/quoteRequestLines/infra/invalidateQuoteRequestLinesDebounced";
import { quoteRequestLinesQuery } from "#modules/quoteRequestLines/quoteRequestLinesQuery";
import { quoteRequestsQuery } from "#modules/quoteRequests/infra/quoteRequests.query";
import { queryKeys } from "#shared/queryKeys/queryKeys";
import {
  assertUnreachable,
  PartAnalyzeCompletedPayload,
  PartAnalyzeUpdatedPayload,
  WebhookPayload,
} from "shared";

export const applyRealtimeEvent = (payload: WebhookPayload, queryClient: QueryClient) => {
  switch (payload.event) {
    case "analyze.updated": {
      applyPartAnalyzeUpdated(payload, queryClient);
      return;
    }
    case "analyze.completed": {
      applyPartAnalyzed(payload, queryClient);
      return;
    }
    case "analyze.failed": {
      const { partId } = payload.data;
      void invalidatePart(queryClient, { partId });
      return;
    }
    case "prediction.started": {
      payload.data.quoteRequestLineIds.forEach((quoteRequestLineId) =>
        refreshQuoteRequestLine(queryClient, quoteRequestLineId),
      );
      return;
    }
    case "prediction.completed":
    case "prediction.failed": {
      refreshQuoteRequestLine(queryClient, payload.data.quoteRequestLineId);
      return;
    }
    case "quoteRequestLine.updated": {
      payload.data.quoteRequestLineIds.forEach((quoteRequestLineId) =>
        invalidateQuoteRequestLinesDebounced({ queryClient, quoteRequestLineId }),
      );
      return;
    }
    case "nesting.updated": {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.layoutNestings.detail(payload.data.layoutId),
      });
      payload.data.quoteRequestLineIds.forEach((quoteRequestLineId) =>
        invalidateQuoteRequestLinesDebounced({ queryClient, quoteRequestLineId }),
      );
      return;
    }
    case "inbound.received": {
      void queryClient.invalidateQueries({
        queryKey: [quoteRequestLinesQuery().queryKey[0]],
      });
      void queryClient.invalidateQueries({
        queryKey: [quoteRequestsQuery().queryKey[0]],
      });
      return;
    }
    default: {
      assertUnreachable(payload);
    }
  }
};

const refreshQuoteRequestLine = (queryClient: QueryClient, quoteRequestLineId: string) => {
  void queryClient.cancelQueries({
    queryKey: queryKeys.quoteRequestLines.detail(quoteRequestLineId),
    exact: true,
  });
  invalidateQuoteRequestLinesDebounced({ queryClient, quoteRequestLineId });
};

const refetchUncachedPart = (queryClient: QueryClient, partId: string) => {
  void queryClient
    .cancelQueries({ queryKey: queryKeys.parts.detail(partId), exact: true })
    .then(() =>
      queryClient.invalidateQueries({ queryKey: queryKeys.parts.detail(partId), exact: true }),
    );
};

const updatePart = ({
  queryClient,
  partId,
  partUpdate,
}: {
  queryClient: QueryClient;
  partId: string;
  partUpdate?: Partial<PartDto>;
}) => {
  const existingPart = getPartQueryData(queryClient, partId);
  if (!existingPart) {
    refetchUncachedPart(queryClient, partId);
    return;
  }

  const newPart: PartDto = {
    ...existingPart,
    ...partUpdate,
  };

  setPartQueryData(queryClient, partId, newPart);
};

const applyPartAnalyzed = (payload: PartAnalyzeCompletedPayload, queryClient: QueryClient) => {
  updatePart({
    queryClient,
    partId: payload.data.partId,
    partUpdate: payload.data.part,
  });
  updateQuoteRequestLinesForPart(queryClient, payload.data.partId, payload.data.part);
};

const applyPartAnalyzeUpdated = (payload: PartAnalyzeUpdatedPayload, queryClient: QueryClient) => {
  switch (payload.subType) {
    case "gltf": {
      updatePart({
        queryClient,
        partId: payload.data.partId,
        partUpdate: { gltfStatus: payload.data.gltfStatus, urlGltf: payload.data.gltfUrl },
      });
      return;
    }
    case "pdfAnnotated": {
      updatePart({
        queryClient,
        partId: payload.data.partId,
        partUpdate: { urlPdfAnnotated: payload.data.pdfAnnotatedUrl },
      });
      return;
    }
    case "pdfPreview": {
      updatePart({
        queryClient,
        partId: payload.data.partId,
        partUpdate: {
          pdfPreviewStatus: payload.data.pdfPreviewStatus,
          urlPdfPreview: payload.data.pdfPreviewUrl,
        },
      });
      return;
    }
    case "preview": {
      updatePart({
        queryClient,
        partId: payload.data.partId,
        partUpdate: {
          previewStatus: payload.data.previewStatus,
          ...getPartFieldToUpdate(payload.data.source, payload.data.previewUrl),
        },
      });
      return;
    }
    case "analyze2d": {
      const { partId } = payload.data;
      void invalidatePart(queryClient, { partId });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.parts.features(partId),
      });
      return;
    }
    case "drawingEmbedding": {
      updatePart({
        queryClient,
        partId: payload.data.partId,
        partUpdate: {
          hasDrawingEmbedding: payload.data.hasDrawingEmbedding,
          drawingEmbeddingStatus: payload.data.drawingEmbeddingStatus,
        },
      });
      return;
    }
    case "embedding": {
      updatePart({
        queryClient,
        partId: payload.data.partId,
        partUpdate: {
          hasEmbedding: payload.data.hasEmbedding,
          embeddingStatus: payload.data.embeddingStatus,
        },
      });
      return;
    }
    case "topologicalEmbedding": {
      updatePart({
        queryClient,
        partId: payload.data.partId,
        partUpdate: {
          hasTopologicalEmbedding: payload.data.hasTopologicalEmbedding,
          topologicalEmbeddingStatus: payload.data.topologicalEmbeddingStatus,
        },
      });
      return;
    }
    case "child": {
      addChildrenToQuoteRequestDebounced({
        queryClient,
        quoteRequestId: payload.data.quoteRequestId,
        children: [
          {
            partId: payload.data.childId,
            quoteRequestLineId: payload.data.childQuoteRequestLineId,
          },
        ],
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.parts.withChildren(payload.data.partId),
      });
      return;
    }
    case "features": {
      const { partId } = payload.data;
      void invalidatePart(queryClient, { partId });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.parts.features(partId),
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.parts.weldLength(partId),
      });
      return;
    }
    case "legacyChildren": {
      addChildrenToQuoteRequestDebounced({
        queryClient,
        quoteRequestId: payload.data.quoteRequestId,
        children: payload.data.children,
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.parts.withChildren(payload.data.partId),
      });
      return;
    }
    default: {
      assertUnreachable(payload);
    }
  }
};

const getPartFieldToUpdate = (source: "2d" | "3d", url: string) => {
  switch (source) {
    case "3d": {
      return { urlPng3D: url };
    }
    case "2d": {
      return { urlPng2D: url };
    }
    default: {
      assertUnreachable(source);
    }
  }
};
